import type { Campaign, Subtask, Task } from '../types'

export const operationalHubs = ['CONO', 'NORDESTE', 'LESTE', 'SUDESTE'] as const
export type OperationalHub = typeof operationalHubs[number]

export interface HubOperationalMetric {
  hub: OperationalHub
  volume: number
  pages: number
  pageTarget: number
  composition: Record<'Diagramação' | 'Revisão' | 'Criação' | 'Gestão', number>
  highlight: string
}

export interface HubOperationalSnapshot {
  start: string
  end: string
  volume: number
  operationalEffort: number
  mediaProduction: number
  pages: number
  pageTarget: number
  hubs: HubOperationalMetric[]
}

const pageTargets: Record<OperationalHub, number> = { CONO: 360, NORDESTE: 364, LESTE: 200, SUDESTE: 200 }
const mediaFormats = new Set<Subtask['format']>(['LED', 'Carrossel', 'TikTok/Reels', 'TV', 'YouTube', 'Anúncio', 'Cartela Live'])

export function operationalHubFor(operation?: string): OperationalHub | undefined {
  const normalized = (operation ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase()
  if (normalized.includes('CONO')) return 'CONO'
  if (normalized.includes('NORDESTE') || /\bNE\b/.test(normalized)) return 'NORDESTE'
  if (normalized.includes('LESTE')) return 'LESTE'
  if (normalized.includes('SUDESTE') || normalized.includes('ARICANDUVA') || normalized.includes('PENHA')) return 'SUDESTE'
  return undefined
}

function completionDate(task: Task, subtask: Subtask) {
  if (subtask.completedAt) return subtask.completedAt.slice(0, 10)
  return [...task.activityEntries].reverse().find(entry => entry.statusChange?.entity === 'deliverable' && entry.statusChange.entityId === subtask.id && entry.statusChange.to === 'Aprovada')?.occurredOn?.slice(0, 10)
}

function effortGroup(kind: Subtask['kind']): keyof HubOperationalMetric['composition'] {
  if (kind === 'Diagramação') return 'Diagramação'
  if (kind === 'Revisão') return 'Revisão'
  if (kind === 'Planejamento') return 'Gestão'
  return 'Criação'
}

export function buildHubOperationalSnapshot(tasks: Task[], campaigns: Campaign[], start: string, end: string, selectedHubs: OperationalHub[] = [...operationalHubs]): HubOperationalSnapshot {
  const campaignById = new Map(campaigns.map(campaign => [campaign.id, campaign]))
  const selected = new Set(selectedHubs)
  const rows = operationalHubs.map(hub => ({
    hub,
    volume: 0,
    pages: 0,
    pageTarget: pageTargets[hub],
    composition: { Diagramação: 0, Revisão: 0, Criação: 0, Gestão: 0 },
    campaignVolumes: new Map<string, number>(),
  }))
  const byHub = new Map(rows.map(row => [row.hub, row]))
  let mediaProduction = 0

  tasks.forEach(task => {
    const campaign = campaignById.get(task.campaignId)
    if (!campaign || campaign.clientId !== 'assai') return
    const hub = operationalHubFor(campaign.operation)
    if (!hub || !selected.has(hub)) return
    const row = byHub.get(hub)!
    task.subtasks.forEach(subtask => {
      const completedOn = completionDate(task, subtask)
      if (subtask.status !== 'Aprovada' || !completedOn || completedOn < start || completedOn > end) return
      const volume = Math.max(0, Number.isFinite(subtask.volume) ? subtask.volume : 0)
      row.volume += volume
      row.composition[effortGroup(subtask.kind)] += volume
      row.campaignVolumes.set(campaign.name, (row.campaignVolumes.get(campaign.name) ?? 0) + volume)
      if (subtask.format === 'Jornal' || subtask.kind === 'Diagramação') row.pages += Math.max(0, subtask.pages ?? 0)
      if (subtask.materialCategory === 'Mídia ON' || subtask.materialCategory === 'DOOH' || mediaFormats.has(subtask.format)) mediaProduction += volume
    })
  })

  const hubs = rows.filter(row => selected.has(row.hub)).map(({ campaignVolumes, ...row }) => ({
    ...row,
    highlight: [...campaignVolumes.entries()].sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))[0]?.[0] ?? 'Sem destaque no período',
  }))
  const volume = hubs.reduce((total, hub) => total + hub.volume, 0)
  const pages = hubs.reduce((total, hub) => total + hub.pages, 0)
  const pageTarget = hubs.reduce((total, hub) => total + hub.pageTarget, 0)
  const operationalVolume = hubs.reduce((total, hub) => total + hub.composition.Diagramação + hub.composition.Revisão, 0)
  return {
    start,
    end,
    volume,
    operationalEffort: volume ? Math.round(operationalVolume / volume * 100) : 0,
    mediaProduction,
    pages,
    pageTarget,
    hubs,
  }
}

export function metricDelta(current: number, comparison: number) {
  if (!comparison) return current ? 100 : 0
  return Math.round((current - comparison) / comparison * 100)
}
